import { useState, useEffect } from 'react';
import { getAnalytics, type AnalyticsResponse } from '../../shared/api/analytics';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { getUserPostsPath } from '../../shared/config/env';
import { getDisplayTitleForPath, isAboutPage, getPagePathForLang, USER_PAGE_ROUTES } from '../../shared/utils/pageDisplay';
import { pagePathForDisplayLang } from '../../shared/utils/urlUtils';
import './AnalyticsWidget.css';

/**
 * アナリティクスウィジェットコンポーネント
 * Home画面の右カラムに表示されるアクセス解析データ
 */
type TabType = 'pages' | 'countries';

export const AnalyticsWidget = () => {
  const lang = useLanguage();
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('pages');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAnalytics(7, lang); // 過去7日間のデータを取得（言語を指定）
        setAnalytics(data);
      } catch (err) {
        console.warn('Failed to fetch analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [lang]);

  if (loading) {
    return (
      <aside className="analytics-widget">
        <div className="analytics-widget__content">
          <h2 className="analytics-widget__title">
            {lang === 'ja' ? 'アクセス解析' : 'Analytics'}
          </h2>
          <div className="analytics-widget__loading">
            {lang === 'ja' ? '読み込み中...' : 'Loading...'}
          </div>
        </div>
      </aside>
    );
  }

  if (error || !analytics) {
    return (
      <aside className="analytics-widget">
        <div className="analytics-widget__content">
          <h2 className="analytics-widget__title">
            {lang === 'ja' ? 'アクセス解析' : 'Analytics'}
          </h2>
          <div className="analytics-widget__error">
            {lang === 'ja' ? 'データの取得に失敗しました' : 'Failed to load data'}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="analytics-widget">
      <div className="analytics-widget__content">
        <h2 className="analytics-widget__title">
          {lang === 'ja' ? 'アクセス解析' : 'Analytics'}
        </h2>
        
        <div className="analytics-widget__stats">
          <div className="analytics-widget__stat">
            <div className="analytics-widget__stat-value">
              {analytics.total_views.toLocaleString()}
            </div>
            <div className="analytics-widget__stat-label">
              {lang === 'ja' ? '総アクセス数' : 'Total Views'}
            </div>
            <div className="analytics-widget__stat-period">
              {lang === 'ja' 
                ? `過去7日間` 
                : 'Last 7 days'}
            </div>
          </div>
        </div>

        {/* タブ切り替え */}
        <div className="analytics-widget__tabs">
          <button
            className={`analytics-widget__tab ${activeTab === 'pages' ? 'analytics-widget__tab--active' : ''}`}
            onClick={() => setActiveTab('pages')}
          >
            {lang === 'ja' ? '人気ページ' : 'Top Pages'}
          </button>
          <button
            className={`analytics-widget__tab ${activeTab === 'countries' ? 'analytics-widget__tab--active' : ''}`}
            onClick={() => setActiveTab('countries')}
          >
            {lang === 'ja' ? '国別' : 'Top Countries'}
          </button>
        </div>

        {/* Top Pages タブ */}
        {activeTab === 'pages' && analytics.top_pages && analytics.top_pages.length > 0 && (
          <div className="analytics-widget__top-pages">
            <ul className="analytics-widget__pages-list">
              {analytics.top_pages.slice(0, 5).map((page, index) => {
                const siteUrl = getSiteUrl();
                const userPostsPath = getUserPostsPath();
                const postsPath = lang === 'ja' ? `/ja${userPostsPath}` : `/en${userPostsPath}`;
                // フルパスは表示言語に変換。about は共通定義、それ以外は postsPath + スラッグ
                const pathForLang = page.page_path.startsWith('/')
                  ? pagePathForDisplayLang(page.page_path, lang)
                  : (isAboutPage(page.page_path) ? getPagePathForLang(USER_PAGE_ROUTES.about.path, lang) : `${postsPath}/${page.page_path}`);
                const fullUrl = `${siteUrl}${pathForLang}`;

                // 既知ページは共通の表示名（About 等）。それ以外は API title または page_path
                const knownTitle = getDisplayTitleForPath(page.page_path, lang);
                const displayTitle = knownTitle ?? (page.title || page.page_path);
                
                return (
                  <li key={index} className="analytics-widget__page-item">
                    <span className="analytics-widget__page-rank">{index + 1}</span>
                    <div className="analytics-widget__page-info">
                      <div className="analytics-widget__page-path">
                        <a
                          href={fullUrl}
                          className="analytics-widget__page-link"
                        >
                          {displayTitle}
                        </a>
                      </div>
                      <div className="analytics-widget__page-views">
                        {page.views.toLocaleString()} {lang === 'ja' ? '回' : 'views'}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Top Countries タブ */}
        {activeTab === 'countries' && analytics.top_countries && analytics.top_countries.length > 0 && (
          <div className="analytics-widget__top-countries">
            <ul className="analytics-widget__countries-list">
              {analytics.top_countries.slice(0, 5).map((country, index) => (
                <li key={index} className="analytics-widget__country-item">
                  <span className="analytics-widget__country-rank">{index + 1}</span>
                  <div className="analytics-widget__country-info">
                    <div className="analytics-widget__country-name">
                      {country.country}
                    </div>
                    <div className="analytics-widget__country-views">
                      {country.views.toLocaleString()} {lang === 'ja' ? '回' : 'views'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* データがない場合のメッセージ */}
        {activeTab === 'pages' && (!analytics.top_pages || analytics.top_pages.length === 0) && (
          <div className="analytics-widget__empty">
            {lang === 'ja' ? 'データがありません' : 'No data available'}
          </div>
        )}

        {activeTab === 'countries' && (!analytics.top_countries || analytics.top_countries.length === 0) && (
          <div className="analytics-widget__empty">
            {lang === 'ja' ? 'データがありません' : 'No data available'}
          </div>
        )}
      </div>
    </aside>
  );
};
